import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import NavButtons from './NavButtons';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_PRODUCTS } from '../../lib/mockData';

export default function Step1Products({ formData, updateFormData, onNext, onBack, corporateClient, orderRules = {} }) {
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [infoProduct, setInfoProduct] = useState(null);

  const isMealPrep  = formData.delivery_type === 'meal_prep';
  const isMeetUp    = formData.delivery_type === 'meet_up';
  const isPortioned = isMealPrep || isMeetUp; // both require a qty ≥ min

  const minQty = isMeetUp
    ? (orderRules.meet_up_min_qty ?? 5)
    : (orderRules.meal_prep_min_qty ?? 5);
  const maxQty = orderRules.meal_prep_max_qty ?? 20;
  const qty    = formData.quantity ?? minQty;

  // Ensure quantity is initialized to the minimum when portioned ordering is active
  useEffect(() => {
    if (isPortioned && (!formData.quantity || formData.quantity < minQty)) {
      updateFormData({ quantity: minQty });
    }
  }, [isPortioned, minQty]);

  useEffect(() => {
    async function fetchProducts() {
      if (isSupabaseConfigured()) {
        try {
          const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('available', true)
            .order('sort_order');
          if (!error && data && data.length > 0) {
            // Normalise: DB stores image_url, frontend expects images[]
            const normalised = data.map(p => ({
              ...p,
              images: p.images || (p.image_url ? [p.image_url] : []),
              sizes: p.sizes || [{ name: 'Standard', price: p.base_price || p.price || 0 }],
              base_price: p.base_price || p.price || 0,
            }));
            setProducts(normalised);
          } else setProducts(MOCK_PRODUCTS);
        } catch (e) {
          setProducts(MOCK_PRODUCTS);
        }
      } else {
        setProducts(MOCK_PRODUCTS);
      }
      setLoading(false);
    }
    fetchProducts();
  }, []);

  const handleSelectProduct = (product) => {
    const isNextDay = formData.delivery_type === 'next_day';
    const price = isNextDay && product.available_next_day && product.next_day_price
      ? Number(product.next_day_price)
      : (product.base_price || 0);

    updateFormData({
      product,
      size: { ...product.sizes[0], price },
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blush-300" />
      </div>
    );
  }

  const portionedQtyValid = !isPortioned || (qty >= minQty);
  const isValid = !!formData.product && !!formData.size && (!corporateClient || (formData.group_size >= 1)) && portionedQtyValid;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-8"
    >
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-serif text-charcoal">What are you ordering?</h2>
        <p className="text-sage-500 font-sans">Fresh, home-cooked and delivered to your door.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {products.map((product) => {
          const isSelected = formData.product?.id === product.id;
          return (
            <div
              key={product.id}
              onClick={() => handleSelectProduct(product)}
              className={`relative cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 ${
                isSelected
                  ? 'ring-2 ring-blush-300 shadow-glow transform -translate-y-1'
                  : 'hover:shadow-soft hover:-translate-y-1 bg-white border border-sage-100'
              }`}
            >
              <div className="aspect-[4/3] relative overflow-hidden bg-sage-50">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                {isSelected && (
                  <div className="absolute top-4 right-4 bg-blush-300 text-white rounded-full p-1.5 shadow-md">
                    <SafeIcon name="Check" className="text-xl" />
                  </div>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setInfoProduct(product); }}
                  className="absolute bottom-3 right-3 w-8 h-8 flex items-center justify-center"
                  title="View details"
                >
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60 animate-ping" />
                  <span className="relative w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm text-red-500">
                    <SafeIcon name="Info" className="text-base" />
                  </span>
                </button>
              </div>
              <div className="p-5 bg-white">
                <h3 className="font-serif text-xl mb-1">{product.name}</h3>
                <p className="text-sm text-sage-400 mb-3 line-clamp-2">{product.description}</p>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-charcoal">
                    ${formData.delivery_type === 'next_day' && product.available_next_day && product.next_day_price
                      ? Number(product.next_day_price).toFixed(2)
                      : Number(product.base_price).toFixed(2)}
                  </p>
                  {formData.delivery_type === 'next_day' && product.available_next_day && product.next_day_price && (
                    <span className="text-xs bg-blush-100 text-blush-600 font-bold px-2 py-1 rounded-full">Next-Day</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>


      {/* Quantity stepper — meal prep & meet-up orders, appears once a product is chosen */}
      {isPortioned && formData.product && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`border rounded-2xl p-5 space-y-4 ${
            isMeetUp
              ? 'bg-blush-50/60 border-blush-100'
              : 'bg-green-50/60 border-green-100'
          }`}
        >
          <div className="flex items-center gap-3">
            <SafeIcon name="Coffee" className={`text-xl shrink-0 ${isMeetUp ? 'text-blush-400' : 'text-green-500'}`} />
            <div>
              <p className="font-bold text-charcoal">How many portions?</p>
              <p className="text-sm text-sage-400">
                Minimum {minQty} portions — maximum {maxQty} per order.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => updateFormData({ quantity: Math.max(minQty, qty - 1) })}
              className="w-10 h-10 rounded-full bg-white border border-sage-200 flex items-center justify-center text-charcoal font-bold hover:border-blush-300 transition-colors"
            >
              −
            </button>
            <span className="text-3xl font-serif text-charcoal w-12 text-center">{qty}</span>
            <button
              type="button"
              onClick={() => updateFormData({ quantity: Math.min(maxQty, qty + 1) })}
              className="w-10 h-10 rounded-full bg-white border border-sage-200 flex items-center justify-center text-charcoal font-bold hover:border-blush-300 transition-colors"
            >
              +
            </button>
            <span className="text-sm text-sage-500">portions</span>
          </div>

          {/* Live price preview */}
          <div className="bg-white border border-sage-100 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-sage-500">
              {qty} × ${Number(formData.size?.price || formData.product?.base_price || 0).toFixed(2)}
            </span>
            <span className="font-serif text-xl text-charcoal">
              ${(qty * Number(formData.size?.price || formData.product?.base_price || 0)).toFixed(2)}
            </span>
          </div>

          {qty < minQty && (
            <p className="text-amber-600 text-xs font-bold uppercase tracking-wider">
              ⚠ Minimum {minQty} portions required to continue.
            </p>
          )}

          {isMeetUp && (
            <p className="text-[10px] text-green-600 uppercase tracking-widest font-bold">
              No delivery fee — pick up at the meet-up location
            </p>
          )}
        </motion.div>
      )}

      {/* Group size — corporate orders only */}
      {corporateClient && (
        <div className="bg-sage-50/50 border border-sage-100 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <SafeIcon name="Users" className="text-blush-300 text-xl" />
            <div>
              <p className="font-bold text-charcoal">How many people are ordering?</p>
              <p className="text-sm text-sage-400">Each person gets one bowl. You'll split the payment at checkout.</p>
            </div>
          </div>
          <div className="flex items-center gap-4 pt-1">
            <button
              type="button"
              onClick={() => updateFormData({ group_size: Math.max(1, (formData.group_size || 1) - 1) })}
              className="w-10 h-10 rounded-full bg-white border border-sage-200 flex items-center justify-center text-charcoal font-bold hover:border-blush-300 transition-colors"
            >
              −
            </button>
            <span className="text-3xl font-serif text-charcoal w-12 text-center">{formData.group_size || 1}</span>
            <button
              type="button"
              onClick={() => updateFormData({ group_size: (formData.group_size || 1) + 1 })}
              className="w-10 h-10 rounded-full bg-white border border-sage-200 flex items-center justify-center text-charcoal font-bold hover:border-blush-300 transition-colors"
            >
              +
            </button>
            <span className="text-sm text-sage-500">
              {(formData.group_size || 1) === 1 ? 'person' : 'people'}
            </span>
          </div>
        </div>
      )}

      <NavButtons
        onNext={onNext}
        onBack={onBack}
        disabled={!isValid}
        nextLabel="Choose Add-ons"
      />

      {/* Product detail modal */}
      {infoProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => setInfoProduct(null)}
        >
          <div className="absolute inset-0 bg-charcoal/40 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {infoProduct.images?.[0] && (
              <div className="aspect-[4/3] bg-sage-50 overflow-hidden">
                <img
                  src={infoProduct.images[0]}
                  alt={infoProduct.name}
                  className="w-full h-full object-cover"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <div className="p-6 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-serif text-2xl text-charcoal leading-tight">{infoProduct.name}</h3>
                <button
                  onClick={() => setInfoProduct(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-sage-50 text-sage-400 hover:bg-sage-100 transition-colors shrink-0 mt-0.5"
                >
                  <SafeIcon name="X" />
                </button>
              </div>
              {infoProduct.description && (
                <p className="text-sage-500 text-sm leading-relaxed whitespace-pre-wrap">{infoProduct.description}</p>
              )}
              <div className="pt-2 border-t border-sage-100 flex items-center gap-3 flex-wrap">
                <span className="font-bold text-charcoal text-lg">
                  ${Number(
                    formData.delivery_type === 'next_day' && infoProduct.available_next_day && infoProduct.next_day_price
                      ? infoProduct.next_day_price
                      : infoProduct.base_price
                  ).toFixed(2)}
                </span>
                {infoProduct.available_next_day && (
                  <span className="text-xs bg-blush-100 text-blush-600 font-bold px-2.5 py-1 rounded-full">
                    ⚡ Available Next-Day
                  </span>
                )}
              </div>
              <button
                onClick={() => { handleSelectProduct(infoProduct); setInfoProduct(null); }}
                className="btn-primary w-full mt-1"
              >
                {formData.product?.id === infoProduct.id ? 'Selected ✓' : 'Select This Item'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
